import styled from 'styled-components';
import { useState, useRef } from 'react';
import { Tooltip } from '@mui/material';
import { ErrorBox } from '../ErrorBox';
import { AttachmentIcon, InfoIcon, RemoveIcon, UploadIcon } from '../../svg';
import { colors } from '../../constants';

const UploadButton = styled.label``;
const RemoveContainer = styled.div``;
const AttachmentTitle = styled.span``;
const FileCard = styled.div``;

interface IProps {
    className?: string;
    file: File | null;
    onFileChange(file: File | null): void;
}

const BaseFileUploader = ({ className, file, onFileChange }: IProps) => {
    const [fileError, setFileError] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    const handleFileChange = () => {
        if (!fileInput.current?.files) return;

        const file = fileInput.current.files[0];

        if (file.size > maxFileSize) {
            onFileChange(null);
            setFileError(true);

            return;
        }

        setFileError(false);
        onFileChange(file);
    };

    const removeFile = () => {
        if (!fileInput.current) return;

        fileInput.current.value = '';
        onFileChange(null);
    };

    const fileTypes = '.pdf, .doc, .docx, .jpg, .jpeg, .png, .gif';
    const maxFileSize = 15000000;

    return (
        <div className={className}>
            <h4>
                Wil je een bijlage toevoegen?
                <Tooltip
                    enterTouchDelay={150}
                    title="Voeg optioneel een bestand toe van maximaal 15mb (pdf, doc, docx, jpg, jpeg, png, gif)"
                >
                    <span style={{ marginLeft: '6px' }}>
                        <InfoIcon />
                    </span>
                </Tooltip>
            </h4>
            {fileError && (
                <ErrorBox>Het bestand is te groot (max 15mb)</ErrorBox>
            )}
            {file && (
                <FileCard>
                    <AttachmentTitle>
                        <AttachmentIcon />
                        {file.name}
                    </AttachmentTitle>
                    <RemoveContainer onClick={removeFile}>
                        <RemoveIcon />
                    </RemoveContainer>
                </FileCard>
            )}
            <UploadButton htmlFor="fileInput">
                <UploadIcon />
                {file ? 'Kies ander bestand' : 'Kies bestand'}
                <input
                    accept={fileTypes}
                    className="file-input"
                    id="fileInput"
                    ref={fileInput}
                    type="file"
                    onChange={handleFileChange}
                />
            </UploadButton>
        </div>
    );
};

export const FileUploader = styled(BaseFileUploader)`
    h4 {
        font-weight: 400;
        margin: 0 0 20px;
        line-height: 1;
    }

    .file-input {
        display: none;
    }

    ${ErrorBox}, ${FileCard} {
        margin-bottom: 20px;
    }

    ${FileCard} {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-left: 20px;
        height: 44px;
        border-radius: 25px;
        font-size: 15px;
        font-weight: 300;
        background-color: ${colors.black};
        color: ${colors.greyBackground};
    }

    ${RemoveIcon}, ${AttachmentIcon} {
        fill: ${colors.greyBackground};
    }

    ${AttachmentIcon} {
        width: 15px;
    }

    ${RemoveIcon} {
        transition: fill 0.1s linear;
    }

    ${RemoveContainer} {
        display: flex;
        padding: 10px 20px;

        :hover {
            cursor: pointer;

            ${RemoveIcon} {
                fill: ${colors.white};
            }
        }
    }

    ${UploadButton} {
        display: flex;
        justify-content: center;
        align-items: center;
        width: fit-content;
        border: 1px solid ${colors.greyMedium};
        border-radius: 3px;
        padding: 10px 20px;
        cursor: pointer;
        transition: border 0.15s linear;

        :hover {
            border: 1px solid ${colors.black};
        }
    }

    ${AttachmentTitle} {
        display: flex;
        align-items: center;
        height: 100%;
    }

    ${UploadIcon}, ${AttachmentIcon} {
        margin-right: 10px;
    }
`;
